import { Box, Stack } from "@mui/material";
import { colorScales, colors } from "@zfp-akademie-a-s/zfp-design";
import { SummaryCard, SummaryCardRow } from "Components/_Shared/SummaryCard";
import type { AssetAllocation } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { Chart as ChartJS } from "chart.js/auto";
import AnnotationPlugin from "chartjs-plugin-annotation";
import React from "react";
import { Pie } from "react-chartjs-2";
import { formatTwoDecimals } from "./helpers";

ChartJS.register(AnnotationPlugin);

type Props = {
  allocation: AssetAllocation;
};

export const GoalsFulfillmentSidebarChart = ({ allocation }: Props) => {
  const { t } = useResource();

  const items = [
    { label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Bonds), value: allocation.bonds, color: colorScales.blue[300] },
    { label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Equities), value: allocation.equities, color: colors.brandPrimary },
    {
      label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Commodities),
      value: allocation.commodities,
      color: colorScales.yellow[300],
    },
    {
      label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.RealEstate),
      value: allocation.realEstate,
      color: colorScales.purple[700],
    },
    { label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Cash), value: allocation.cash, color: colorScales.green[600] },
    { label: t(Resources.AnalysisDetail.GoalsFulfillment.FormFields.Other), value: allocation.others, color: colorScales.gray[600] },
  ];

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const label = context.label || "";
            const value = context.raw || 0;
            return `${label}: ${formatTwoDecimals(value)}%`;
          },
        },
      },
    },
  };

  const data = {
    labels: items.map((i) => i.label),
    datasets: [
      {
        data: items.map((i) => i.value),
        backgroundColor: items.map((i) => i.color),
        borderColor: items.filter((i) => i.value > 0).length > 1 ? "white" : "transparent",
        borderWidth: items.filter((i) => i.value > 0).length > 1 ? 2 : 0,
      },
    ],
  };

  return (
    <Stack width="100%" pt={Spacing.L}>
      <Box height={"170px"}>
        <Pie options={options} data={data} />
      </Box>

      <Box py={Spacing.XXL}>
        <SummaryCard>
          {items.map((item, index) => (
            <React.Fragment key={`${item.label}-${index}`}>
              <SummaryCardRow color={item.color} value={formatTwoDecimals(item.value)} units={"%"} label={item.label} />
            </React.Fragment>
          ))}
        </SummaryCard>
      </Box>
    </Stack>
  );
};
